
import { PropertyData } from '@gentrometer/shared';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { genProperties, downloadPropertiesCSV } from '../../utils/api';
import { useLRU } from '../../utils/useLRU';
import './Properties.css';
import classNames from 'classnames';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

export default function Properties() {
  const propertiesByPage = useLRU<string, Array<PropertyData>>(30);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [moreProperties, setMoreProperties] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterText, setFilterText] = useState<string>('');
  const [debouncedFilterText, setDebouncedFilterText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'for_sale' | 'sold' | 'both'>('sold');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce filter text changes
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedFilterText(filterText);
    }, 100);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filterText]);

  // Concatenate all loaded pages into a single list
  // Use cacheVersion to ensure this re-runs when cache changes
  const cacheVersion = propertiesByPage.cacheVersion;
  const allProperties = useMemo(() => {
    const properties: Array<PropertyData> = [];
    // Filter cache keys that match current status filter and search, and sort by page number
    const searchKey = debouncedFilterText || '';
    const statusKey = statusFilter;
    const matchingKeys = propertiesByPage.keys()
      .filter(key => {
        // Cache key format: `${page}|${search}|${status}`
        const parts = key.split('|');
        if (parts.length !== 3) return false;
        const keySearch = parts[1];
        const keyStatus = parts[2];
        return keyStatus === statusKey && keySearch === searchKey;
      })
      .sort((a, b) => {
        const pageA = parseInt(a.split('|')[0], 10);
        const pageB = parseInt(b.split('|')[0], 10);
        return pageA - pageB;
      });
    for (const key of matchingKeys) {
      const pageProperties = propertiesByPage.get(key);
      if (pageProperties) {
        properties.push(...pageProperties);
      }
    }
    
    return properties;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertiesByPage, statusFilter, debouncedFilterText, cacheVersion]);

  // Reset to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, debouncedFilterText]);

  // Fetch page when currentPage, statusFilter, or debouncedFilterText changes and page is not already loaded
  useEffect(() => {
    // Use | as separator to avoid conflicts with search text that might contain -
    const searchKey = debouncedFilterText || '';
    const cacheKey = `${currentPage}|${searchKey}|${statusFilter}`;
    if (propertiesByPage.has(cacheKey)) {
      return; // Page already loaded
    }

    const loadPage = async () => {
      setLoading(true);
      try {
        const response = await genProperties(currentPage, statusFilter, debouncedFilterText || undefined);
        const pageProperties = response?.properties ?? [];
        propertiesByPage.set(cacheKey, pageProperties);
        setMoreProperties(response?.moreProperties ?? false);
      } catch (error) {
        console.error(`Error loading page ${currentPage}:`, error);
      } finally {
        setLoading(false);
      }
    };

    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, debouncedFilterText]);

  const loadNextPage = () => {
    if (loading || !moreProperties) return;
    setCurrentPage(prev => prev + 1);
  };

  const handleDownloadCSV = async () => {
    try {
      const blob = await downloadPropertiesCSV(
        statusFilter !== 'both' ? statusFilter : undefined,
        debouncedFilterText || undefined
      );
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `properties_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV. Please try again.');
    }
  };

  return (
    <div className="properties-page">
      <h1>Properties</h1>
      <div className="filter-bar">
        <div className="filter-controls">
          <select
            className="status-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'for_sale' | 'sold' | 'both')}
          >
            <option value="both">All Properties</option>
            <option value="for_sale">For Sale</option>
            <option value="sold">Sold</option>
          </select>
          <input
            type="text"
            className="filter-input"
            placeholder="Filter by address, price, status, date..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          <div className="download-button-container">
            <button
              className="download-button"
              onClick={handleDownloadCSV}
              disabled={loading}
              title="Download all matching properties as CSV"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 11L3 6h3V1h4v5h3L8 11zM1 13v2h14v-2H1z" fill="currentColor"/>
              </svg>
              Download CSV
            </button>
            <div className="download-button-tooltip">
              Download all properties matching current filters as CSV
            </div>
          </div>
        </div>
      </div>
      <div className="properties-container">
        {loading && allProperties.length === 0 && (
          <div className="loading-message">Loading properties...</div>
        )}
        {!loading && allProperties.length === 0 && (
          <div className="empty-message">No properties found.</div>
        )}
        {allProperties.length > 0 && (
          <table className="properties-table">
            <thead>
              <tr>
                <th>Address</th>
                <th>Price</th>
                <th>Status</th>
                <th>Listing/Sale Date</th>
                <th>Scrape Date</th>
              </tr>
            </thead>
            <tbody>
              {allProperties.map((property, index) => (
                <tr key={`${property.address}-${index}`}>
                  <td>{property.address}</td>
                  <td>{property.price}</td>
                  <td>
                    <span className={classNames('status-badge', {
                      'status-badge-for-sale': property.status === 'for_sale',
                      'status-badge-sold': property.status === 'sold'
                    })}>
                      {property.status === 'for_sale' ? 'For Sale' : 'Sold'}
                    </span>
                  </td>
                  <td>{formatDate(property.date)}</td>
                  <td>{formatDate(property.scrapeDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {moreProperties && (
          <div className="load-more-container">
            <button 
              className="load-more-button"
              onClick={loadNextPage}
              disabled={loading}>
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}